import {Component, inject, Injector, OnInit} from '@angular/core';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {IonButton, IonCol, IonGrid, IonNavLink, IonRow} from '@ionic/angular/standalone';
import {TranslocoDirective} from '@jsverse/transloco';
import {logger, StorageService, TranslateHelperService} from '@services';
import {FirebaseInitService} from '../../services/shared/firebase-init.service';

@Component({
	           selector: 'app-cookie-consent-banner',
	           standalone: true,
	           templateUrl: './cookie-consent-banner.component.html',
	           styleUrls: ['./cookie-consent-banner.component.scss'],
	           imports: [
			TranslocoDirective,
		           IonGrid,
		           IonRow,
		           IonCol,
		           IonButton,
		           IonNavLink,
		           RouterLink,
		           RouterLinkActive
	           ]
           })
export class CookieConsentBannerComponent implements OnInit {
	cookieChoice: boolean | null = null;
	public shouldRenderBanner = false;

	private readonly injector = inject(Injector);
	private readonly storageService = inject(StorageService);
	public readonly translateService = inject(TranslateHelperService);

	ngOnInit(): void {
		const value = this.storageService.get('cookiesConsent');
		if (value === 'true' || value === 'false') {
			this.cookieChoice = value === 'true';
			this.applyConsent(this.cookieChoice).catch((error) => logger.error(error));
			return;
		}
		this.cookieChoice = null;
		this.scheduleBannerRender();
	}

	cookiesConsent(): void {
		this.applyConsent(true).catch((error) => logger.error(error));
	}

	cookiesReject(): void {
		this.applyConsent(false).catch((error) => logger.error(error));
	}

	private async applyConsent(granted: boolean): Promise<void> {
		this.cookieChoice = granted;
		this.storageService.set('cookiesConsent', granted ? 'true' : 'false');

		if (typeof window === 'undefined') {
			return;
		}

		const [{setConsent}, {setAnalyticsCollectionEnabled}] = await Promise.all([
			import('@firebase/analytics'),
			import('firebase/analytics')
		]);

		try {
			const firebaseInitService = this.injector.get(FirebaseInitService, null);
			const analytics = firebaseInitService?.analytics;
			if (analytics) {
				setAnalyticsCollectionEnabled(analytics, granted);
			}
		} catch (error) {
			logger.warn('FirebaseInitService not available:', error);
		}

		setConsent({
			           analytics_storage: granted ? 'granted' : 'denied',
			           ad_storage: granted ? 'granted' : 'denied',
			           ad_personalization: granted ? 'granted' : 'denied',
			           ad_user_data: granted ? 'granted' : 'denied',
			           functionality_storage: granted ? 'granted' : 'denied',
			           personalization_storage: granted ? 'granted' : 'denied',
			           security_storage: granted ? 'granted' : 'denied'
		           });
	}

	hasUserChoice(): boolean {
		return this.cookieChoice !== null;
	}

	private scheduleBannerRender(): void {
		if (this.shouldRenderBanner || this.cookieChoice !== null) {
			return;
		}
		if (typeof window === 'undefined' || !window.requestAnimationFrame) {
			this.shouldRenderBanner = true;
			return;
		}
		// Delay banner render to avoid affecting LCP (Largest Contentful Paint)
		// Wait for page to be fully interactive
		setTimeout(() => {
			if (this.cookieChoice === null && typeof window !== 'undefined') {
				// Only render after LCP has been determined (typically after 2-3 seconds)
				window.requestAnimationFrame(() => {
					if (this.cookieChoice === null) {
						this.shouldRenderBanner = true;
					}
				});
			}
		}, 3000); // 3 second delay to ensure LCP is not the banner
	}
}
